import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { messages as api, social, verification, type ConversationDto, type MessagingSettingsDto, type PrivateMessageDto } from '../net/api';
import { Sheet } from './Sheet';

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const nameOf = (m: { username: string; displayName: string | null }) => m.displayName ?? m.username;

/** Private messages: chats with friends and group chats. Text and emoji only. */
export function Messages({ onClose, onRead }: { onClose: () => void; onRead: () => void }) {
  const [list, setList] = useState<ConversationDto[]>();
  const [settings, setSettings] = useState<MessagingSettingsDto>();
  const [open, setOpen] = useState<number>();
  const [view, setView] = useState<'list' | 'new' | 'group' | 'settings'>('list');
  const [msg, setMsg] = useState<string>();
  const load = useCallback(() => api.list().then((r) => (setList(r.conversations), setSettings(r.settings)), (e: Error) => setMsg(e.message)), []);
  useEffect(() => {
    void load();
  }, [load]);

  const current = list?.find((c) => c.id === open);
  if (current && current.myStatus === 'active') {
    return (
      <Sheet title={current.name ?? 'Chat'} subtitle={current.kind === 'group' ? `${current.members.filter((m) => m.status === 'active').length} people` : undefined} onClose={onClose} wide>
        <Conversation c={current} onBack={() => { setOpen(undefined); void load(); onRead(); }} onChanged={() => void load()} />
      </Sheet>
    );
  }
  return (
    <Sheet title="Messages" subtitle="Private chats with friends" onClose={onClose}>
      {msg && <p className="form-error">{msg}</p>}
      {view === 'new' && <NewChat onOpen={(id) => { setView('list'); void load().then(() => setOpen(id)); }} onBack={() => setView('list')} />}
      {view === 'group' && <NewGroup onOpen={(id) => { setView('list'); void load().then(() => setOpen(id)); }} onBack={() => setView('list')} />}
      {view === 'settings' && settings && <Settings s={settings} onChanged={() => void load()} onBack={() => setView('list')} />}
      {view === 'list' && settings && (
        <div className="stack">
          {!settings.canMessage ? (
            <p className="muted small-print">
              {settings.age !== null && settings.age < 13
                ? 'Private messages are for players 13 and over.'
                : 'Private messages are for verified players. Get verified from Your account, or ask your parent to allow messaging with a friend.'}
            </p>
          ) : (
            <div className="row-wrap">
              <button className="btn small" onClick={() => setView('new')}>New chat</button>
              {settings.verified && <button className="btn small ghost" onClick={() => setView('group')}>New group</button>}
              <button className="btn small ghost" onClick={() => setView('settings')}>Settings</button>
            </div>
          )}
          {list && !list.length && <p className="muted small-print">No chats yet.</p>}
          <ul className="convo-list">
            {list?.map((c) => (
              <li key={c.id}>
                {c.myStatus === 'active' ? (
                  <button className="convo" onClick={() => setOpen(c.id)}>
                    <b>{c.name}</b>
                    {c.unread > 0 && <span className="badge">{c.unread}</span>}
                    <span className="muted small-print">{c.last ? c.last.text : 'No messages yet'}</span>
                  </button>
                ) : c.myStatus === 'invited' ? (
                  <div className="convo">
                    <b>{c.name}</b>
                    <span className="small-print">You’re invited to this group chat.</span>
                    <div className="row-wrap">
                      <button className="btn small" onClick={() => void api.answerInvite(c.id, true).then(load)}>Join</button>
                      <button className="btn small ghost" onClick={() => void api.answerInvite(c.id, false).then(load)}>No thanks</button>
                    </div>
                  </div>
                ) : (
                  <div className="convo">
                    <b>{c.name}</b>
                    <span className="muted small-print">Waiting for a parent to approve.</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="muted small-print">Text and emoji only: links, emails and phone numbers aren’t sent. Our team and parents of players under 17 can read private messages.</p>
        </div>
      )}
    </Sheet>
  );
}

function Conversation({ c, onBack, onChanged }: { c: ConversationDto; onBack: () => void; onChanged: () => void }) {
  const [rows, setRows] = useState<PrivateMessageDto[]>([]);
  const [monitored, setMonitored] = useState(c.monitored);
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<string>();
  const [manage, setManage] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const load = useCallback(() => api.read(c.id).then((r) => (setRows(r.messages), setMonitored(r.monitored)), () => {}), [c.id]);
  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(id);
  }, [load]);
  useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [rows.length]);
  async function send(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const r = await api.send(c.id, text);
      setNotice(r.notice);
      setText('');
      await load();
    } catch (err) {
      setNotice(errText(err));
    }
  }
  async function report(m: PrivateMessageDto) {
    const unsafe = window.confirm(`Report this message from ${m.from}?\n\nOK = it made me feel unsafe (asked to meet, or to share private things)\nCancel = choose another reason`);
    const category = unsafe ? 'unsafe' : window.prompt('What was wrong? Type: harassment, language, scam or other', 'harassment')?.trim().toLowerCase();
    if (!category) return;
    try {
      await api.report(m.id, ['unsafe', 'harassment', 'language', 'scam', 'other', 'personal_info'].includes(category) ? category : 'other');
      setNotice('Thanks. Our team will look at it.');
    } catch (err) {
      setNotice(errText(err));
    }
  }
  return (
    <div className="stack chat-view">
      {monitored && <p className="monitored-banner" role="note">This chat is monitored by a parent or guardian of one of the people in it.</p>}
      <div className="row-wrap">
        <button className="btn small ghost" onClick={onBack}>← All chats</button>
        {c.kind === 'group' && <button className="btn small ghost" onClick={() => setManage((x) => !x)}>{manage ? 'Back to chat' : 'People'}</button>}
      </div>
      {manage ? (
        <GroupPeople c={c} onChanged={onChanged} onLeft={onBack} />
      ) : (
        <>
          <div className="messages-log" aria-live="polite">
            {rows.map((m) => (
              <div key={m.id} className={`pm ${m.status}`}>
                <span className="pm-from">{m.from}</span>
                <span className="pm-text">{m.status === 'blocked' ? <s>{m.text}</s> : m.text}</span>
                {m.status === 'blocked' && <span className="muted small-print">Not sent</span>}
                {m.status !== 'blocked' && <button className="link-btn small-print pm-report" onClick={() => void report(m)} aria-label={`Report message from ${m.from}`}>Report</button>}
              </div>
            ))}
            {!rows.length && <p className="muted small-print">Say hi! Text and emoji only.</p>}
            <div ref={end} />
          </div>
          <form className="row-wrap" onSubmit={send}>
            <input className="grow" aria-label="Message" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message (text and emoji only)" />
            <button className="btn small">Send</button>
          </form>
          {notice && <p className="form-error small-print" role="status">{notice}</p>}
        </>
      )}
    </div>
  );
}

function GroupPeople({ c, onChanged, onLeft }: { c: ConversationDto; onChanged: () => void; onLeft: () => void }) {
  const [name, setName] = useState('');
  const [msg, setMsg] = useState<string>();
  const add = async () => {
    try {
      const r = await api.addMember(c.id, name);
      setMsg(r.status === 'active' ? `${r.name} joined.` : r.status === 'invited' ? `${r.name} got an invite.` : `${r.name} is waiting for a parent to approve.`);
      setName('');
      onChanged();
    } catch (e) {
      setMsg(errText(e));
    }
  };
  return (
    <div className="stack">
      <ul className="plain">
        {c.members.map((m) => (
          <li key={m.userId} className="row-wrap">
            <span>{nameOf(m)}</span>
            {m.status !== 'active' && <span className="muted small-print">{m.status === 'invited' ? 'invited' : 'waiting for a parent'}</span>}
            {c.isOwner && m.status !== 'left' && <button className="link-btn small-print" onClick={() => void api.removeMember(c.id, m.userId).then(onChanged)}>Remove</button>}
          </li>
        ))}
      </ul>
      <div className="row-wrap">
        <input className="grow" aria-label="Friend's name" placeholder="Friend's name" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="btn small" disabled={!name.trim()} onClick={() => void add()}>Add</button>
      </div>
      <p className="muted small-print">You can add your friends, and friends of people in the group (they get an invite). Up to 8 people.</p>
      {msg && <p className="note-ok" role="status">{msg}</p>}
      <button className="btn small ghost" onClick={() => void api.leave(c.id).then(onLeft)}>Leave group</button>
    </div>
  );
}

function useFriends() {
  const [friends, setFriends] = useState<{ username: string; displayName: string | null }[]>();
  useEffect(() => {
    social.overview().then((s) => setFriends(s.friends), () => setFriends([]));
  }, []);
  return friends;
}

function NewChat({ onOpen, onBack }: { onOpen: (id: number) => void; onBack: () => void }) {
  const friends = useFriends();
  const [msg, setMsg] = useState<string>();
  return (
    <div className="stack">
      <button className="btn small ghost" onClick={onBack}>← Back</button>
      <p className="small-print">Message a friend:</p>
      <ul className="plain">
        {friends?.map((f) => (
          <li key={f.username}>
            <button className="link-btn" onClick={() => void api.direct(f.username).then((r) => onOpen(r.id), (e: Error) => setMsg(e.message))}>{nameOf(f)}</button>
          </li>
        ))}
        {friends && !friends.length && <li className="muted small-print">Add some friends first.</li>}
      </ul>
      {msg && <p className="form-error" role="status">{msg}</p>}
    </div>
  );
}

function NewGroup({ onOpen, onBack }: { onOpen: (id: number) => void; onBack: () => void }) {
  const friends = useFriends();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [msg, setMsg] = useState<string>();
  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const r = await api.createGroup(name, picked);
      const refused = r.added.filter((a) => a.status === 'refused');
      if (refused.length) setMsg(refused.map((a) => `${a.name}: ${a.reason}`).join(' '));
      onOpen(r.id);
    } catch (err) {
      setMsg(errText(err));
    }
  }
  return (
    <form className="stack" onSubmit={create}>
      <button type="button" className="btn small ghost" onClick={onBack}>← Back</button>
      <label>
        Group name
        <input value={name} maxLength={30} onChange={(e) => setName(e.target.value)} required />
      </label>
      <fieldset>
        <legend>Friends to add (up to 7)</legend>
        {friends?.map((f) => (
          <label key={f.username} className="check">
            <input
              type="checkbox"
              checked={picked.includes(f.username)}
              disabled={!picked.includes(f.username) && picked.length >= 7}
              onChange={(e) => setPicked((p) => (e.target.checked ? [...p, f.username] : p.filter((x) => x !== f.username)))}
            />{' '}
            {nameOf(f)}
          </label>
        ))}
      </fieldset>
      <p className="muted small-print">You can create up to 5 groups and be in up to 10. Group names follow the naming rules.</p>
      {msg && <p className="form-error" role="status">{msg}</p>}
      <button className="btn small" disabled={name.trim().length < 2}>Create group</button>
    </form>
  );
}

function Settings({ s, onChanged, onBack }: { s: MessagingSettingsDto; onChanged: () => void; onBack: () => void }) {
  const [sent, setSent] = useState<string>();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string>();
  return (
    <div className="stack">
      <button className="btn small ghost" onClick={onBack}>← Back</button>
      <label className="check">
        <input type="checkbox" checked={s.inviteOnly} onChange={(e) => void api.setInviteOnly(e.target.checked).then(onChanged)} /> Only add me to group chats by invite
      </label>
      {s.canOptOut && (
        <div className="stack">
          <h3 className="mini-head">Talking with other 17+ players</h3>
          {s.ageOptOut ? (
            <>
              <p className="small-print">You’ve turned off the age-group rule: you can message other 17+ players who did the same.</p>
              <button className="btn small ghost" onClick={() => void api.setAgeOptOut(false).then(onChanged)}>Turn the age-group rule back on</button>
            </>
          ) : (
            <>
              <p className="muted small-print">Private messages are normally only within your age group. Players 17 and over can turn that off to talk with other 17+ players who did too. Confirm your email to do it (not possible with an active warning or ban).</p>
              <div className="row-wrap">
                <button className="btn small ghost" onClick={() => void verification.emailCode().then((r) => setSent(r.to), (e: Error) => setMsg(e.message))}>Email me a code</button>
                {sent && <span className="muted small-print">Sent to {sent}.</span>}
              </div>
              {sent && (
                <div className="row-wrap">
                  <input aria-label="Code from the email" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
                  <button className="btn small" onClick={() => void api.setAgeOptOut(true, code).then(onChanged, (e: Error) => setMsg(e.message))}>Turn it off</button>
                </div>
              )}
            </>
          )}
          {msg && <p className="form-error" role="status">{msg}</p>}
        </div>
      )}
    </div>
  );
}
